// Grant MANUAL do painel: marcado com `source: 'manual'` (dados novos) ou, no
// legado, sem nenhum rastro de compra — webhooks sempre gravam subGuruId /
// hotmartSubscriptionCode / productGuruId / transactionIdentifier (Guru/IAP)
// ou `status` (Pagarme).
export const isManualGrant = (product) =>
  product?.source === 'manual' ||
  (!product?.subGuruId &&
    !product?.hotmartSubscriptionCode &&
    !product?.productGuruId &&
    !product?.transactionIdentifier &&
    !product?.hotmartSubscriptionCode &&
    !product?.status);

/** Entrada em `user.productIds` concedida/revogada por webhook ou loja (não painel). */
export const isPurchaseLinkedProductEntry = (product) =>
  !!product && !isManualGrant(product);

/** Curso Stripe em `user.courses`: compra não traz `source`; painel grava `source: 'manual'`. */
export const isManualStripeCourseEntry = (course) => course?.source === 'manual';

const STRIPE_SUBSCRIPTION_MANAGED_STATUSES = ['active', 'trialing', 'past_due'];

/** Product id do plano dentro de um doc `users/{uid}/subscriptions/*` (extensão Firebase+Stripe). */
export const stripeSubscriptionProductId = (subscription) =>
  subscription?.items?.[0]?.plan?.product ??
  subscription?.items?.[0]?.price?.product ??
  null;

/** Assinatura Stripe ativa (ou em tolerância) vinculada a este plano. */
export const hasActiveStripeSubscriptionForPlan = (subscriptions, productId) =>
  (subscriptions || []).some(
    (sub) =>
      stripeSubscriptionProductId(sub) === productId &&
      STRIPE_SUBSCRIPTION_MANAGED_STATUSES.includes(sub.status)
  );

/**
 * Plano em `user.plan` concedido pelo painel — não por assinatura Stripe ativa.
 * Novos grants gravam `planSource: 'manual'`; legado = plan bate e não há sub ativa desse produto.
 */
export const isManualStripePlanAccess = (user, productId, subscriptions) =>
  user?.plan === productId &&
  (user?.planSource === 'manual' ||
    !hasActiveStripeSubscriptionForPlan(subscriptions, productId));

export const getPlatformForProduct = (product) => {
  // A ORIGEM do registro vem antes do formato do código: um grant manual de
  // produto Guru saía como "Guru" na planilha e a cliente não conseguia
  // distingui-lo do acesso real da assinatura (ticket de suporte).
  if (isManualGrant(product)) return "Manual";
  if (product?.transactionIdentifier) return "IAP";
  if (/^[a-zA-Z0-9]{20}$/.test(product.productId)) return "Pagarme";
  if (/^\d+$/.test(product.productId)) return "Guru";
  return "-";
};

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Data de cadastro do aluno = criação da CONTA no Authentication (`authCreatedAt`,
// anexada pela tela de alunos via callable). O `created_at` do doc é só fallback:
// em docs legados ele guarda a data do acesso manual/webhook que o sobrescreveu,
// e não a data em que o aluno se cadastrou (ticket de suporte: conta de fev/2025
// aparecia no painel como nov/2025).
export const getSignupDate = (user) => {
  if (user?.authCreatedAt) return new Date(user.authCreatedAt);
  return toJsDate(user?.created_at);
};

// Carimbo do doc MUITO posterior ao cadastro = aquela gravação não criou a conta,
// então o que ela afirmou sobre a ORIGEM do cadastro (isSignUpFromAdminPanel) não
// vale. Sem a data do Auth não há como acusar nada — a flag segue valendo.
const STAMP_TOLERANCE_MS = 24 * 60 * 60 * 1000;
export const originStampIsStale = (user) => {
  if (!user?.authCreatedAt) return false;
  const stored = toJsDate(user?.created_at);
  return !!stored && stored.getTime() - user.authCreatedAt > STAMP_TOLERANCE_MS;
};

export const getProductName = (productId) => {
  if (typeof productId === 'string' && productId.startsWith("iap")) {
    if (/\.monthly\.(\d+)$/.test(productId)) {
      return `IAP - Plano Mensal ${productId.match(/\.monthly\.(\d+)$/)[1]}`;
    }
    if (/\.quarterly\.(\d+)$/.test(productId)) {
      return `IAP - Plano Trimestral ${productId.match(/\.quarterly\.(\d+)$/)[1]}`;
    }
    if (/\.semiannual\.(\d+)$/.test(productId)) {
      return `IAP - Plano Semestral ${productId.match(/\.semiannual\.(\d+)$/)[1]}`;
    }
    if (/\.yearly\.(\d+)$/.test(productId)) {
      return `IAP - Plano Anual ${productId.match(/\.yearly\.(\d+)$/)[1]}`;
    }
    return "IAP";
  }

  return productId || "Produto Inválido";
};

//lógica de acesso a módulo de conteúdos
const toDate = (seconds) => new Date(seconds * 1000);

export function hasProductAccess(cardData, userData) {
  const cardPlans = cardData?.plans?.map((plan) => plan.productId);
  const cardCourses = cardData?.coursesCard?.map((course) => course.productId);
  const cardProductIds = cardData?.productIds;
  const isUnrestrictedChannel =
    (!Array.isArray(cardPlans) || cardPlans.length === 0) &&
    (!Array.isArray(cardCourses) || cardCourses.length === 0) &&
    (!Array.isArray(cardProductIds) || cardProductIds.length === 0);

  const userPlan = userData?.plan;
  const userCourses = userData?.courses?.map((course) => course.productId);

  // `!!(...)`: sem isto, quando nada libera acesso E userData é nullish (aluno
  // anônimo), o resultado era `userData?.isAdmin` = `undefined`, não `false` —
  // funciona igual em `if`/`&&`, mas vaza como valor inconsistente (ex.: some
  // some falsy diferente ao logar ou serializar).
  return !!(
    // Produto com data válida
    (cardProductIds &&
      cardProductIds.length > 0 &&
      cardProductIds.some((cardProductId) => {
        const matchingUserProducts = userData?.productIds?.filter(
          (product) => product.productId === cardProductId
        );
        if (!matchingUserProducts || matchingUserProducts.length === 0) {
          return false;
        }

        return matchingUserProducts.some((userProduct) => {
          return (
            !userProduct?.expirationDate ||
            toDate(userProduct.expirationDate.seconds) > new Date()
          );
        });
      })) ||

    // Plano
    (cardPlans &&
      cardPlans.length > 0 &&
      cardPlans.some((planId) => planId === userPlan)) ||

    // Curso com validade
    (userCourses &&
      userCourses.length > 0 &&
      userCourses.some((userCourseId) =>
        cardCourses?.some(
          (cardCourseId) =>
            cardCourseId === userCourseId &&
            toDate(
              userData?.courses?.find(
                (course) => course.productId === cardCourseId
              )?.dueDate?.seconds
            ) > new Date()
        )
      )) ||

    // módulo gratuito
    isUnrestrictedChannel ||

    // Admin sempre tem acesso
    userData?.isAdmin
  );
}

export function userHasAnyAccess(userData) {
  if (!userData) return false;
  if (userData.isAdmin) return true;

  // Produto ativo
  if (Array.isArray(userData.productIds)) {
    for (const p of userData.productIds) {
      if (!p.expirationDate || toDate(p.expirationDate.seconds) > new Date()) {
        return true;
      }
    }
  }

  // Plano ativo
  if (userData.plan) {
    return true;
  }

  // Curso com validade
  if (Array.isArray(userData.courses)) {
    for (const c of userData.courses) {
      if (c.dueDate && toDate(c.dueDate.seconds) > new Date()) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Um "produto de vitrine" pode reunir vários productIds/plans/courses que
 * liberam exatamente o mesmo conteúdo (turmas/lançamentos diferentes do
 * mesmo curso). getCatalogGroup normaliza os ids próprios do doc (ownIds) +
 * os vínculos manuais (equivalentIds, outros docs) numa única lista.
 */
export function getCatalogGroup(product) {
  if (product?.accessIds?.length) return product.accessIds;

  return [
    ...(product?.ownIds?.length
      ? product.ownIds
      : [{ id: product?.productId, type: product?.type }]),
    ...(product?.equivalentIds ?? []),
  ];
}

function buildFakeCardFromGroup(group) {
  return {
    productIds: group.filter((g) => g.type === 'product').map((g) => g.id),
    plans: group.filter((g) => g.type === 'plan').map((g) => ({ productId: g.id })),
    coursesCard: group.filter((g) => g.type === 'course').map((g) => ({ productId: g.id })),
  };
}

/**
 * Reaproveita hasProductAccess (fonte única de permissionamento) alimentando-a
 * com um "card sintético" formado pelo grupo de ids do produto de vitrine —
 * nenhuma lógica de acesso nova é criada aqui.
 */
export function userHasCatalogProductAccess(product, userData) {
  return hasProductAccess(buildFakeCardFromGroup(getCatalogGroup(product)), userData);
}

/**
 * Um produto ainda é "vendável" se pelo menos um id do grupo (turma/lançamento)
 * não estiver arquivado. archivedById é um lookup {productId -> archived}
 * construído a partir de todos os produtos/planos/cursos carregados.
 */
export function isCatalogProductSellable(product, archivedById) {
  return getCatalogGroup(product).some(({ id }) => !archivedById[id]);
}

/**
 * Regra de exibição na vitrine: quem já tem acesso sempre vê (mesmo se
 * arquivado); quem não tem acesso só vê enquanto o produto ainda for vendável.
 */
export function shouldShowCatalogProduct(product, userData, archivedById) {
  if (!product?.catalogImage?.url) return false;
  if (!product?.productCategoryId) return false;
  if (product?.showInCatalog === false) return false;
  return (
    userHasCatalogProductAccess(product, userData) ||
    isCatalogProductSellable(product, archivedById)
  );
}

/** Filtro de exibição (não de permissão): o card pertence ao grupo deste produto? */
export function cardBelongsToCatalogGroup(card, group) {
  const productIds = group.filter((g) => g.type === 'product').map((g) => g.id);
  const planIds = group.filter((g) => g.type === 'plan').map((g) => g.id);
  const courseIds = group.filter((g) => g.type === 'course').map((g) => g.id);

  const cardProductIds = card?.productIds ?? [];
  const cardPlanIds = (card?.plans ?? []).map((p) => p.productId);
  const cardCourseIds = (card?.coursesCard ?? []).map((c) => c.productId);

  return (
    cardProductIds.some((id) => productIds.includes(id)) ||
    cardPlanIds.some((id) => planIds.includes(id)) ||
    cardCourseIds.some((id) => courseIds.includes(id))
  );
}
